/**
 * Poly Hydration — CloudWatch RUM Telemetry Integration
 *
 * Emits hydration outcome data to CloudWatch RUM as custom events.
 * Covers both mismatch (irreconcilable/attribute) and success cases
 * so we can track reconciliation rates across deployments.
 *
 * The RUM web client (cwr) is loaded asynchronously by the cathode script.
 * If it hasn't initialized by the time hydration runs, events will be
 * silently dropped. This is acceptable — we prioritize hydration speed
 * over telemetry completeness, and the RUM client is typically available
 * by the time DOMContentLoaded fires.
 */

import type { MismatchRecord } from './types';

declare global {
  // CloudWatch RUM web client global function
  // eslint-disable-next-line no-var
  var cwr: ((command: string, payload: unknown) => void) | undefined;
}

/**
 * Emits a hydration mismatch record to CloudWatch RUM as a custom event.
 *
 * Safe to call even if the RUM client hasn't loaded yet — the call is
 * a no-op when cwr is unavailable. Some early mismatch events may be
 * lost due to the async race between RUM initialization and hydration
 * execution. This is an acceptable tradeoff: hydration must not block
 * on telemetry availability.
 */
export function emitMismatchToRum(record: MismatchRecord): void {
  if (typeof cwr !== 'function') {
    return;
  }

  try {
    cwr('recordEvent', {
      type: 'poly_hydration_mismatch',
      data: {
        blockId: record.blockId,
        componentType: record.componentType,
        phaseOneChildList: record.phaseOne.childListChanges,
        phaseOneClass: record.phaseOne.classChanges,
        phaseOneStyle: record.phaseOne.styleChanges,
        phaseOneTotal: record.phaseOne.totalMutations,
        phaseTwoChildList: record.phaseTwo.childListChanges,
        phaseTwoClass: record.phaseTwo.classChanges,
        phaseTwoStyle: record.phaseTwo.styleChanges,
        phaseTwoTotal: record.phaseTwo.totalMutations,
        totalMutations: record.phaseOne.totalMutations + record.phaseTwo.totalMutations,
        irreconcilable: record.phaseOne.totalMutations > 0,
      },
    });
  } catch (e) {
    // Telemetry errors must never break hydration
  }
}

/**
 * Emits a successful hydration event to CloudWatch RUM.
 *
 * Called when a block completes hydration without errors, regardless of
 * whether it took the reconcilable or irreconcilable path. This lets us
 * track overall hydration success rates alongside mismatch data.
 */
export function emitSuccessToRum(blockId: string, componentType: string, path: 'reconciled' | 'clean-render' | 'client-only'): void {
  if (typeof cwr !== 'function') {
    return;
  }

  try {
    cwr('recordEvent', {
      type: 'poly_hydration_success',
      data: {
        blockId,
        componentType,
        path,
      },
    });
  } catch (e) {
    // Telemetry errors must never break hydration
  }
}
